import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Message } from 'app/layout/common/messages/messages.types';
import { SocketService } from 'app/core/services/socket.service';
import { catchError, map, Observable, of, ReplaySubject, Subject, Subscription, switchMap, take, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MessagesService implements OnDestroy {
    private _messages: ReplaySubject<Message[]> = new ReplaySubject<Message[]>(1);
    private _newMessage$ = new Subject<Message>();
    private _socketSub: Subscription;

    /**
     * Emits each time a new real-time message arrives (for toast container)
     */
    get newMessage$(): Observable<Message> {
        return this._newMessage$.asObservable();
    }

    constructor(
        private _httpClient: HttpClient,
        private _socketService: SocketService,
    ) {
        this._setupSocketListeners();
    }

    ngOnDestroy(): void {
        this._socketSub?.unsubscribe();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get messages$(): Observable<Message[]> {
        return this._messages.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all messages from the real notifications API
     */
    getAll(): Observable<Message[]> {
        return this._httpClient.get<any>('api/notifications').pipe(
            map(response => {
                // Backend returns { notifications, unreadCount, total }
                const notifications = response.notifications || response;
                return Array.isArray(notifications) ? notifications : [];
            }),
            tap((messages) => {
                this._messages.next(messages);
            }),
            catchError(() => {
                this._messages.next([]);
                return of([]);
            })
        );
    }

    /**
     * Create a message (legacy — kept for backward compat)
     */
    create(message: Message): Observable<Message> {
        return this.messages$.pipe(
            take(1),
            switchMap((messages) =>
                this._httpClient
                    .post<Message>('api/common/messages', { message })
                    .pipe(
                        map((newMessage) => {
                            this._messages.next([...messages, newMessage]);
                            return newMessage;
                        })
                    )
            )
        );
    }

    /**
     * Update the message
     */
    update(id: string, message: Message): Observable<Message> {
        return this.messages$.pipe(
            take(1),
            switchMap((messages) =>
                this._httpClient
                    .patch<Message>('api/common/messages', { id, message })
                    .pipe(
                        map((updatedMessage: Message) => {
                            const index = messages.findIndex((item) => item.id === id);
                            if (index > -1) {
                                messages[index] = updatedMessage;
                            }
                            this._messages.next(messages);
                            return updatedMessage;
                        })
                    )
            )
        );
    }

    /**
     * Delete the message (soft-delete via notifications API)
     */
    delete(id: string): Observable<boolean> {
        return this.messages$.pipe(
            take(1),
            switchMap((messages) =>
                this._httpClient
                    .delete<any>(`api/notifications/${id}`)
                    .pipe(
                        map(() => {
                            const index = messages.findIndex((item) => item.id === id);
                            if (index > -1) {
                                messages.splice(index, 1);
                            }
                            this._messages.next(messages);
                            return true;
                        })
                    )
            )
        );
    }

    /**
     * Mark all messages as read
     */
    markAllAsRead(): Observable<boolean> {
        return this.messages$.pipe(
            take(1),
            switchMap((messages) =>
                this._httpClient
                    .post<any>('api/notifications/read-all', {})
                    .pipe(
                        map(() => {
                            messages.forEach((m) => { m.read = true; });
                            this._messages.next(messages);
                            return true;
                        })
                    )
            )
        );
    }

    /**
     * Mark a single message as read via Socket.IO (optimistic + server)
     */
    markAsReadViaSocket(id: string): void {
        // Optimistic local update
        this.messages$.pipe(take(1)).subscribe(messages => {
            const msg = messages.find(m => m.id === id);
            if (msg) {
                msg.read = true;
                this._messages.next([...messages]);
            }
        });
        // Notify server
        this._socketService.emit('notification:read', { notificationId: parseInt(id, 10) });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _setupSocketListeners(): void {
        this._socketSub = this._socketService.notification$.subscribe(msg => {
            this._addOrReplaceMessage(msg);
            this._newMessage$.next(msg);
        });
    }

    /**
     * If msg.entity_id matches an existing message, replace it in-place.
     * Otherwise prepend to the list.
     */
    private _addOrReplaceMessage(msg: Message): void {
        this.messages$.pipe(take(1)).subscribe(messages => {
            if (msg.entity_id) {
                const idx = messages.findIndex(
                    m => m.entity_id === msg.entity_id && m.entity_type === msg.entity_type
                );
                if (idx > -1) {
                    messages[idx] = { ...msg, read: false };
                    this._messages.next([...messages]);
                    return;
                }
            }
            this._messages.next([msg, ...messages]);
        });
    }
}
